import React, { useCallback, useState } from "react";

import { Box } from "@mui/material";
import { Place } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import "mapbox-gl/dist/mapbox-gl.css";
import Map, {
  AttributionControl,
  Layer,
  MapEvent,
  MapLayerMouseEvent,
  MapStyleDataEvent,
  Marker,
  Source,
} from "react-map-gl";

import { getSectorBaseLayer } from "./layers";
import { useMapStyle } from "./config";
import { SiteMapOverlay } from "./SiteMapOverlay";
import { HelpBox } from "@/components/atoms";
import { Site, SiteCatchment } from "@/types";

type SiteMapProps = {
  sites: Site[];
  catchment: SiteCatchment[];
  loading: boolean;
};

const SiteMap: React.FC<SiteMapProps> = ({ sites, catchment, loading }) => {
  const theme = useTheme();
  const mapStyle = useMapStyle(theme.palette.mode);
  const mapHeight = 544;

  const [firstTime, setFirstTime] = useState<boolean>(true);

  const onLoad = useCallback(
    (event: MapEvent) => {
      const map = event.target;
      map &&
        catchment.forEach((value) => {
          map.setFeatureState(
            {
              id: value.sector_gid,
              source: "sector-source",
              sourceLayer: "postal_sector_test",
            },
            { score: value.score, name: value.sector, category: value.category }
          );
        });
    },
    [catchment]
  );

  const onStyleData = useCallback((event: MapStyleDataEvent) => {
    setFirstTime(false);
  }, []);

  const onClick = useCallback((event: MapLayerMouseEvent) => {
    const feature = event.features && event.features[0];
  }, []);

  return (
    <Box
      height={mapHeight}
      minHeight={mapHeight}
      position="relative"
      sx={{
        overflow: "hidden",
        border: 1,
        borderRadius: 1,
        borderColor: theme.palette.divider,
        ".mapbox-improve-map": {
          display: "none",
        },
        ".mapboxgl-ctrl.mapboxgl-ctrl-attrib": {
          backgroundColor: "transparent !important",
        },
        ".mapboxgl-ctrl-attrib-inner": {
          color: theme.palette.text.secondary,
        },
        ".mapboxgl-ctrl-attrib a": {
          color: theme.palette.text.secondary,
        },
      }}
    >
      <Map
        initialViewState={{
          longitude: -4.3,
          latitude: 54.8,
          zoom: 4,
        }}
        projection={{ name: "mercator" }}
        minZoom={4}
        maxBounds={[
          [-30, 36],
          [30, 66],
        ]}
        mapStyle={mapStyle}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_MAP_TOKEN}
        onClick={onClick}
        onLoad={onLoad}
        onStyleData={onStyleData}
        interactiveLayerIds={["sector-base"]}
        attributionControl={false}
        touchPitch={false}
        dragRotate={false}
        interactive={true}
      >
        <AttributionControl customAttribution=" " />
        <Source
          id="sector-source"
          type="vector"
          url="mapbox://mpilarczykstarcount.postal_sector_test"
        >
          <Layer beforeId="waterway-label" {...getSectorBaseLayer(theme)} />
        </Source>
        {sites.map((site) => (
          <Marker
            key={site.site_id}
            latitude={site.latitude}
            longitude={site.longitude}
            anchor="bottom"
          >
            <HelpBox title={site.site_name}>
              <Place
                sx={{
                  fontSize: 36,
                  color: "info.main",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "scale(1.2)"
                  }
                }}
              />
            </HelpBox>
          </Marker>
        ))}
        <SiteMapOverlay catchment={catchment} firstTime={firstTime} sites={sites} loading={loading}/>
      </Map>
    </Box>
  );
};

export default SiteMap;
